import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * useIdleTimer — Session inactivity timeout hook
 *
 * Security requirement: If a logged-in user is inactive for `idleTimeout` ms,
 * show a warning dialog. If still no response after `warningTimeout` ms,
 * auto-logout gracefully.
 *
 * Idle timer resets on: mousemove, mousedown, keypress, scroll, touchstart
 *
 * @param {Object} options
 * @param {number}   options.idleTimeout    - ms before showing warning (default: 30 min — Sandvik policy)
 * @param {number}   options.warningTimeout - ms after warning before auto-logout (default: 60000 = 60s)
 * @param {Function} options.onIdle         - called when idle timeout reached (show modal)
 * @param {Function} options.onLogout       - called when warning timeout expires (auto-logout)
 * @param {boolean}  options.enabled        - whether the timer is active (only for authenticated users)
 *
 * D6b (2026-08-24). Default idleTimeout raised from 5 min to 30 min per
 * Sandvik session-logout policy. This is the AUTH session timer for
 * logged-in features (favorites, product compare) and is COMPLETELY
 * SEPARATE from the offline-first data-refresh doctrine — never conflate
 * the two. The 60-second warning window is unchanged.
 */
export default function useIdleTimer({
  idleTimeout = 30 * 60 * 1000,
  warningTimeout = 60 * 1000,
  onIdle,
  onLogout,
  enabled = true,
} = {}) {
  const [isIdle, setIsIdle] = useState(false);
  const [isWarning, setIsWarning] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const idleTimerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);

  const clearAllTimers = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
  }, []);

  const resetIdleTimer = useCallback(() => {
    if (!enabled) return;

    clearAllTimers();
    setIsIdle(false);
    setIsWarning(false);
    setCountdown(0);

    idleTimerRef.current = setTimeout(() => {
      setIsIdle(true);
      setIsWarning(true);
      setCountdown(Math.ceil(warningTimeout / 1000));
      onIdle?.();

      // Start countdown
      const startTime = Date.now();
      countdownIntervalRef.current = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(0, Math.ceil((warningTimeout - elapsed) / 1000));
        setCountdown(remaining);

        if (remaining <= 0) {
          clearInterval(countdownIntervalRef.current);
        }
      }, 1000);

      // Auto-logout after warning timeout
      warningTimerRef.current = setTimeout(() => {
        clearAllTimers();
        setIsIdle(true);
        setIsWarning(false);
        onLogout?.();
      }, warningTimeout);
    }, idleTimeout);
  }, [enabled, idleTimeout, warningTimeout, onIdle, onLogout, clearAllTimers]);

  /** User clicked "Stay logged in" */
  const stayActive = useCallback(() => {
    clearAllTimers();
    setIsIdle(false);
    setIsWarning(false);
    setCountdown(0);
    resetIdleTimer();
  }, [clearAllTimers, resetIdleTimer]);

  useEffect(() => {
    if (!enabled) {
      clearAllTimers();
      return;
    }

    const events = ['mousemove', 'mousedown', 'keypress', 'scroll', 'touchstart'];
    const handleActivity = () => {
      if (!isWarning) {
        resetIdleTimer();
      }
    };

    events.forEach(event => document.addEventListener(event, handleActivity, { passive: true }));
    resetIdleTimer();

    return () => {
      events.forEach(event => document.removeEventListener(event, handleActivity));
      clearAllTimers();
    };
  }, [enabled, isWarning, resetIdleTimer, clearAllTimers]);

  return {
    isIdle,
    isWarning,
    countdown,
    stayActive,
    resetIdleTimer,
  };
}
